import { useEffect, useMemo, useState, type FC } from 'react';

import { useTranslation } from '@refinedev/core';

import {
  ActionIcon,
  Accordion,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';

import type { Claim } from 'interfaces';

import { getStatusChipPresentation } from 'utils/badgeColors';
import { getOutlineControlProps } from 'utils/buttonUtils';
import { compareDateDesc } from 'utils/dateUtils';
import { getDisplayNameFromStoredAttachmentName } from 'utils/intakeAttachmentKey';
import type { IntakeItem, IntakeItemDocument } from '@billing/validation';

interface LinkedClaimsPanelProps {
  claims: Claim[];
  intakeItem?: IntakeItem | null;
  documents?: IntakeItemDocument[] | null;
  uploadClaimsWindowTarget?: string;
  canAccessClaims?: boolean;
  canOpenLinkedClaims?: boolean;
}

interface LinkedClaimsDocumentGroup {
  documentID: string;
  documentName: string;
  claims: Claim[];
  parseFailed: boolean;
}

const getPatientName = (claim: Claim, unknownPatientLabel: string) => {
  const name = [claim.patient?.firstName, claim.patient?.lastName]
    .filter((value) => Boolean(value && value.trim()))
    .join(' ')
    .trim();
  return name || unknownPatientLabel;
};

const sortClaimsByCreatedAtDesc = (claims: Claim[]) =>
  claims.sort((firstClaim, secondClaim) =>
    compareDateDesc(firstClaim.createdAt, secondClaim.createdAt)
  );

const getClaimsForDocument = ({
  document,
  claimsByID,
  assignedClaimIDs,
}: {
  document: IntakeItemDocument;
  claimsByID: Map<string, Claim>;
  assignedClaimIDs: Set<string>;
}): Claim[] => {
  const documentClaims: Claim[] = [];

  for (const { claimID } of document.confidenceSummary?.claims ?? []) {
    if (!claimID || assignedClaimIDs.has(claimID)) continue;

    const claim = claimsByID.get(claimID);
    if (!claim) continue;

    assignedClaimIDs.add(claimID);
    documentClaims.push(claim);
  }

  return documentClaims;
};

const getDocumentClaimGroups = ({
  claims,
  documents,
}: {
  claims: Claim[];
  documents: IntakeItemDocument[] | null | undefined;
}): LinkedClaimsDocumentGroup[] | null => {
  if (!documents || documents.length <= 1) {
    return null;
  }

  const claimsByID = new Map(claims.map((claim) => [claim.id, claim]));
  const assignedClaimIDs = new Set<string>();
  const documentGroups: LinkedClaimsDocumentGroup[] = [];

  for (const document of documents) {
    const documentClaims = getClaimsForDocument({
      document,
      claimsByID,
      assignedClaimIDs,
    });
    const parseFailed = document.status === 'failed';
    if (!documentClaims.length && !parseFailed) continue;

    documentGroups.push({
      documentID: document.id,
      documentName: getDisplayNameFromStoredAttachmentName(document.fileName),
      claims: sortClaimsByCreatedAtDesc(documentClaims),
      parseFailed,
    });
  }

  const unassignedClaims = claims.filter(
    (claim) => !assignedClaimIDs.has(claim.id)
  );

  if (!unassignedClaims.length) return documentGroups;

  return [
    ...documentGroups,
    {
      documentID: 'unassigned',
      documentName: '',
      claims: sortClaimsByCreatedAtDesc(unassignedClaims),
      parseFailed: false,
    },
  ];
};

export const LinkedClaimsPanel: FC<LinkedClaimsPanelProps> = ({
  claims,
  intakeItem,
  documents,
  uploadClaimsWindowTarget,
  canAccessClaims = false,
  canOpenLinkedClaims = false,
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');
  const [expanded, setExpanded] = useState(true);
  const [expandedDocumentGroupIDs, setExpandedDocumentGroupIDs] = useState<
    string[]
  >([]);
  const unknownPatientLabel = translate(
    'pages:uploads.uploadsModal.unknownPatient',
    'Unknown patient'
  );
  const unknownStatusLabel = translate(
    'pages:uploads.uploadsModal.unknownStatus',
    'Unknown'
  );

  const sortedClaims = useMemo(
    () => sortClaimsByCreatedAtDesc(claims),
    [claims]
  );
  const documentClaimGroups = useMemo(
    () => getDocumentClaimGroups({ claims, documents }),
    [claims, documents]
  );

  useEffect(() => {
    setExpandedDocumentGroupIDs(
      documentClaimGroups?.map((group) => group.documentID) ?? []
    );
  }, [documentClaimGroups]);

  const canCollapseDocumentGroups = expandedDocumentGroupIDs.length > 0;
  const toggleAriaLabel = expanded
    ? translate(
        'pages:uploads.uploadsModal.collapseLinkedClaims',
        'Collapse linked claims'
      )
    : translate(
        'pages:uploads.uploadsModal.expandLinkedClaims',
        'Expand linked claims'
      );

  const claimsFilterHref = useMemo(() => {
    if (!intakeItem?.id) return '/claims';

    const params = new URLSearchParams();
    params.set('filters', `intakeItemID:${intakeItem.id}`);
    return `/claims?${params.toString()}`;
  }, [intakeItem?.id]);

  const renderClaim = (claim: Claim) => (
    <Card key={claim.id} withBorder radius="sm" padding="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} lineClamp={1}>
            {claim.claimNumber && claim.claimNumber.trim()
              ? translate('pages:uploads.uploadsModal.claimNumber', {
                  claimNumber: claim.claimNumber,
                })
              : translate('pages:uploads.uploadsModal.claimID', {
                  claimID: claim.id.slice(0, 8),
                })}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {getPatientName(claim, unknownPatientLabel)}
          </Text>
        </Stack>
        <Stack gap="xs" align="flex-end">
          {claim.status ? (
            <Badge
              variant="light"
              radius="xl"
              style={{
                backgroundColor: getStatusChipPresentation(
                  claim.status,
                  computedColorScheme
                ).backgroundColor,
                border: `1px solid ${
                  getStatusChipPresentation(claim.status, computedColorScheme)
                    .borderColor
                }`,
                color: getStatusChipPresentation(
                  claim.status,
                  computedColorScheme
                ).textColor,
              }}
            >
              {claim.status}
            </Badge>
          ) : (
            <Badge variant="light" color="gray">
              {unknownStatusLabel}
            </Badge>
          )}
          {canOpenLinkedClaims ? (
            <Button
              size="xs"
              component="a"
              href={`/claims/edit/${claim.id}`}
              target="_blank"
              rel="noreferrer"
              {...getOutlineControlProps(computedColorScheme, false)}
            >
              {translate('buttons.open', 'Open')}
            </Button>
          ) : null}
        </Stack>
      </Group>
    </Card>
  );

  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" align="center">
        <Text fw={600}>
          {translate('pages:uploads.table.linkedClaims', 'Linked Claims')}
        </Text>
        <Group gap="xs" align="center">
          {intakeItem?.id && canAccessClaims ? (
            <Button
              size="xs"
              component="a"
              href={claimsFilterHref}
              target={uploadClaimsWindowTarget}
              variant="light"
              color="blue"
              data-testid="view-filtered-claims-link"
            >
              {translate(
                'pages:uploads.uploadsModal.viewFilteredClaims',
                'View in Claims Table'
              )}
            </Button>
          ) : null}
          {documentClaimGroups?.length ? (
            <Button
              size="xs"
              variant="light"
              color="gray"
              onClick={() => setExpandedDocumentGroupIDs([])}
              disabled={!canCollapseDocumentGroups}
            >
              {translate(
                'pages:uploads.uploadsModal.collapseAllDocuments',
                'Collapse All'
              )}
            </Button>
          ) : null}
          <Badge variant="light" color="blue">
            {sortedClaims.length}
          </Badge>
          <ActionIcon
            size="lg"
            variant="subtle"
            aria-label={toggleAriaLabel}
            onClick={() => setExpanded((value) => !value)}
          >
            <IconChevronDown
              size={20}
              style={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 150ms ease',
              }}
            />
          </ActionIcon>
        </Group>
      </Group>

      <Collapse in={expanded}>
        <Stack gap="xs" mt="sm">
          {documentClaimGroups?.length ? (
            <Accordion
              multiple
              value={expandedDocumentGroupIDs}
              onChange={setExpandedDocumentGroupIDs}
              variant="contained"
            >
              {documentClaimGroups.map((group) => {
                const groupLabel =
                  group.documentName ||
                  translate(
                    'pages:uploads.uploadsModal.otherLinkedClaims',
                    'Other Linked Claims'
                  );

                return (
                  <Accordion.Item
                    key={group.documentID}
                    value={group.documentID}
                  >
                    <Accordion.Control>
                      <Group
                        justify="space-between"
                        gap="xs"
                        wrap="nowrap"
                        pr="xs"
                      >
                        <Text size="sm" fw={600} lineClamp={1}>
                          {groupLabel}
                        </Text>
                        {group.parseFailed ? (
                          <Badge variant="light" color="red">
                            {translate(
                              'pages:uploads.uploadsModal.parseFailed',
                              'Parse failed'
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="light" color="blue">
                            {group.claims.length}
                          </Badge>
                        )}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {group.claims.length ? (
                        <Stack gap="xs">{group.claims.map(renderClaim)}</Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          {translate(
                            'pages:uploads.uploadsModal.noClaimsForDocument',
                            'No claims were created from this document.'
                          )}
                        </Text>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : sortedClaims.length ? (
            sortedClaims.map(renderClaim)
          ) : (
            <Text size="sm" c="dimmed">
              {translate(
                'pages:uploads.uploadsModal.noLinkedClaims',
                'No claims linked to this upload.'
              )}
            </Text>
          )}
        </Stack>
      </Collapse>
    </Card>
  );
};
